import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen, Zap, Clock, Trophy, Star, Mic } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_ICONS = {
  flashcards: Zap,
  lectures: Mic,
  study_time: Clock,
  achievements: Trophy,
};

const STAT_COLORS = {
  flashcards: 'from-amber-500 to-orange-500',
  lectures: 'from-blue-500 to-cyan-500',
  study_time: 'from-emerald-500 to-teal-500',
  achievements: 'from-purple-500 to-indigo-500',
};

const TestimonialsCarousel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const autoRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/testimonials?limit=8`);
        const data = await res.json();
        setTestimonials(data.testimonials || []);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (testimonials.length <= 1) return;
    autoRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(autoRef.current);
  }, [testimonials.length]);

  const goTo = (dir) => {
    clearInterval(autoRef.current);
    setCurrentIndex(prev => {
      if (dir === 'next') return (prev + 1) % testimonials.length;
      return prev === 0 ? testimonials.length - 1 : prev - 1;
    });
  };

  if (loading || testimonials.length === 0) return null;

  // Show 3 at a time on desktop
  const visibleCount = typeof window !== 'undefined' && window.innerWidth >= 768 ? 3 : 1;
  const visible = [];
  for (let i = 0; i < visibleCount; i++) {
    visible.push(testimonials[(currentIndex + i) % testimonials.length]);
  }

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10" data-testid="testimonials-section">
      <div className="text-center mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-[var(--isp-heading)]">Students Love iStudyPro</h2>
        <p className="text-sm text-[var(--isp-muted)] mt-1">Real results from real students</p>
      </div>

      <div className="relative">
        {/* Navigation Arrows */}
        {testimonials.length > visibleCount && (
          <>
            <button onClick={() => goTo('prev')} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white dark:bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] shadow-lg flex items-center justify-center hover:bg-[var(--isp-glass-inner)] transition-colors" data-testid="testimonial-prev">
              <ChevronLeft className="w-4 h-4 text-[var(--isp-heading)]" />
            </button>
            <button onClick={() => goTo('next')} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white dark:bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] shadow-lg flex items-center justify-center hover:bg-[var(--isp-glass-inner)] transition-colors" data-testid="testimonial-next">
              <ChevronRight className="w-4 h-4 text-[var(--isp-heading)]" />
            </button>
          </>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
          <AnimatePresence mode="popLayout">
            {visible.map((t, i) => {
              const Icon = STAT_ICONS[t.stat_type] || Star;
              const gradient = STAT_COLORS[t.stat_type] || 'from-blue-500 to-cyan-500';
              return (
                <motion.div
                  key={`${t.name}-${t.highlight}-${currentIndex + i}`}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                  className="isp-card p-5 flex flex-col"
                  data-testid={`testimonial-card-${i}`}
                >
                  {/* User */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold text-sm`}>
                      {t.initial}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[var(--isp-heading)]">{t.name}</p>
                      <p className="text-[10px] text-[var(--isp-muted)]">iStudyPro Student</p>
                    </div>
                  </div>

                  {/* Quote */}
                  <p className="text-sm text-[var(--isp-body)] leading-relaxed flex-1 mb-4">"{t.quote}"</p>

                  {/* Stats Bar */}
                  <div className="flex items-center gap-3 pt-3 border-t border-[var(--isp-divider)]">
                    <div className="px-2.5 py-1 bg-[#0A0A0A] text-white text-[10px] font-bold flex items-center gap-1">
                      <Icon className="w-3 h-3" /> {t.highlight}
                    </div>
                    <div className="flex gap-3 text-[10px] text-[var(--isp-muted)]">
                      {t.stats.lectures > 0 && <span>{t.stats.lectures} lectures</span>}
                      {t.stats.study_hours > 0 && <span>{t.stats.study_hours}h studied</span>}
                      {t.stats.xp > 0 && <span>{t.stats.xp} XP</span>}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Dots */}
        {testimonials.length > visibleCount && (
          <div className="flex justify-center gap-1.5 mt-4">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => { clearInterval(autoRef.current); setCurrentIndex(i); }}
                className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-[#002FA7] w-5' : 'bg-[var(--isp-glass-border)]'}`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
